import { buildTenantHostname, getPublicAuthApexHostname } from '@/features/auth/lib/tenant-host'
import {
  readPostLogoutSkipAuthApexRedirectFlag,
  readTenantHandoffFailedFlag,
} from '@/features/auth/lib/constants'
import { isTenantHandoffBounceLoop } from '@/features/auth/lib/tenant-handoff'

/**
 * Apex `localStorage` hint for the last tenant (or platform-admin) subdomain the
 * user successfully handed off to. Host-only cookies are not shared between
 * `app.localhost` and `{sub}.localhost`, so a cold apex tab often has no session —
 * this pointer lets us open the tenant host where the durable jar lives.
 */
export const LAST_TENANT_SUBDOMAIN_KEY = 'bf_last_tenant_subdomain' as const

/**
 * Per-tab guard on the apex origin: only auto-resume once. Prevents
 * apex → tenant (no session) → soft-bounce → apex loops.
 */
export const APEX_TENANT_RESUME_ATTEMPTED_KEY =
  'bf_apex_tenant_resume_attempted' as const

export function rememberLastTenantSubdomain(subdomain: string): void {
  const ts = subdomain.trim().toLowerCase()
  if (!ts) return
  try {
    globalThis.localStorage?.setItem(LAST_TENANT_SUBDOMAIN_KEY, ts)
  } catch {
    // ignore quota / private mode
  }
}

export function clearLastTenantSubdomain(): void {
  try {
    globalThis.localStorage?.removeItem(LAST_TENANT_SUBDOMAIN_KEY)
  } catch {
    // ignore
  }
  try {
    globalThis.sessionStorage?.removeItem(APEX_TENANT_RESUME_ATTEMPTED_KEY)
  } catch {
    // ignore
  }
}

export function readLastTenantSubdomain(): string | null {
  try {
    const raw = globalThis.localStorage?.getItem(LAST_TENANT_SUBDOMAIN_KEY)
    if (!raw) return null
    const ts = raw.trim().toLowerCase()
    return ts.length > 0 ? ts : null
  } catch {
    return null
  }
}

function hasResumeAttempted(): boolean {
  try {
    return (
      globalThis.sessionStorage?.getItem(APEX_TENANT_RESUME_ATTEMPTED_KEY) ===
      '1'
    )
  } catch {
    return false
  }
}

function markResumeAttempted(): void {
  try {
    globalThis.sessionStorage?.setItem(APEX_TENANT_RESUME_ATTEMPTED_KEY, '1')
  } catch {
    // ignore
  }
}

/**
 * True when a cold apex tab can open the last known tenant host.
 * Does not navigate — use {@link tryResumeLastTenantFromApex} to start it.
 */
export function canResumeLastTenantFromApex(): boolean {
  if (globalThis.window === undefined) return false

  const { hostname } = globalThis.location
  const apex = getPublicAuthApexHostname(hostname)
  if (apex !== hostname) return false

  if (readPostLogoutSkipAuthApexRedirectFlag()) return false
  if (readTenantHandoffFailedFlag()) return false
  if (isTenantHandoffBounceLoop()) return false
  if (hasResumeAttempted()) return false

  const subdomain = readLastTenantSubdomain()
  if (!subdomain) return false

  return buildTenantHostname(hostname, subdomain) !== hostname
}

/**
 * When the apex has no usable session cookies, navigate once to the last
 * known tenant host so that host's cookies can hydrate the session.
 *
 * @returns true if a navigation was started (caller should render a spinner).
 */
export function tryResumeLastTenantFromApex(): boolean {
  if (!canResumeLastTenantFromApex()) return false

  const { protocol, hostname, port } = globalThis.location
  const subdomain = readLastTenantSubdomain()
  if (!subdomain) return false

  const targetHost = buildTenantHostname(hostname, subdomain)
  markResumeAttempted()
  const portSuffix = port ? `:${port}` : ''
  globalThis.location.replace(`${protocol}//${targetHost}${portSuffix}/`)
  return true
}
